/*
 * Google Map, configured entirely from data attributes on
 * #google-map-canvas — see content/component-google-map.php.
 *
 * The Maps JS API is loaded on demand with the modern dynamic-library
 * bootstrap, so a page without a map never pays for the script.
 * Swap `MAP_STYLE` for any theme from https://snazzymaps.com/.
 */

const MAP_STYLE = [
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#000000" }, { lightness: 17 }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#000000" }, { lightness: 20 }] },
  { featureType: "road.highway", elementType: "geometry.fill", stylers: [{ color: "#000000" }, { lightness: 17 }] },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#000000" }, { lightness: 29 }, { weight: 0.2 }],
  },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#000000" }, { lightness: 18 }] },
  { featureType: "road.local", elementType: "geometry", stylers: [{ color: "#000000" }, { lightness: 16 }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#000000" }, { lightness: 21 }] },
  {
    elementType: "labels.text.stroke",
    stylers: [{ visibility: "on" }, { color: "#000000" }, { lightness: 16 }],
  },
  {
    elementType: "labels.text.fill",
    stylers: [{ saturation: 36 }, { color: "#000000" }, { lightness: 40 }],
  },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "transit", elementType: "geometry", stylers: [{ color: "#000000" }, { lightness: 19 }] },
  { featureType: "administrative", elementType: "geometry.fill", stylers: [{ color: "#000000" }, { lightness: 20 }] },
  {
    featureType: "administrative",
    elementType: "geometry.stroke",
    stylers: [{ color: "#000000" }, { lightness: 17 }, { weight: 1.2 }],
  },
];

/** Google's official bootstrap loader, inlined so there is no CDN <script> in the head. */
function loadMapsApi(key) {
  if (window.google?.maps?.importLibrary) {
    return;
  }

  const params = new URLSearchParams({ key, v: "weekly", libraries: "maps,marker" });
  let promise;

  window.google ??= {};
  window.google.maps ??= {};
  window.google.maps.importLibrary = (library) => {
    promise ??= new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?${params}&callback=__googleMapsCallback`;
      script.async = true;
      script.onerror = () => reject(new Error("Could not load the Google Maps API"));
      window.__googleMapsCallback = resolve;
      document.head.append(script);
    });

    return promise.then(() => window.google.maps.importLibrary(library));
  };
}

export default async function initGoogleMap() {
  const canvas = document.getElementById("google-map-canvas");

  if (!canvas) {
    return;
  }

  const { apiKey, lat, lng, zoom, icon, address } = canvas.dataset;

  if (!apiKey) {
    console.warn("Google Maps API key is missing — set it in _inc/config.php.");
    return;
  }

  loadMapsApi(apiKey);

  const position = { lat: Number(lat), lng: Number(lng) };
  const { Map } = await window.google.maps.importLibrary("maps");
  const { Marker } = await window.google.maps.importLibrary("marker");

  const map = new Map(canvas, {
    center: position,
    zoom: Number(zoom) || 17,
    disableDoubleClickZoom: true,
    mapTypeControl: true,
    scaleControl: true,
    streetViewControl: true,
    styles: MAP_STYLE,
  });

  /*
   * Marker is superseded by AdvancedMarkerElement, but advanced markers
   * require a cloud-styled mapId and that is incompatible with the inline
   * `styles` array above. Keep the classic marker while we style in code.
   */
  new Marker({ map, position, title: address, ...(icon ? { icon } : {}) });
}
